<script lang="ts">
  // Components
  import Media from '../media/Media.svelte';
  import Location from '$icons/LocationIcon.svelte';
  import DefaultTag from '$lib/components/common/tagging/tags/Default.svelte';
  import UserAvatarWrapper from './userAvatarWrapper.svelte';
  // Stores & utils
  import { me } from '$stores/user.store';
  import { goto } from '$app/navigation';
  import { followUser, unfollowUser } from '$utils/api/user.util';

  interface IUserCard {
    _id: string;
    coverImage: string;
    profileImage: string;
    name: string;
    userName: string;
    location: string;
    about: string;
    interests: string[];
    userTotalPosts: number;
    followersAmount: number;
    userTotalEarnedSeed: number;
    firstPosts: any[];
    followers: string[];
  }

  export let user: IUserCard = null;
  export let containerCls = '';
  export let displayFollow = true;

  let isHandlingFollow = false;

  $: isFollowed = user?.followers?.includes($me?._id);
  displayFollow = user?._id !== $me?._id;

  const handleFollow = async () => {
    const res = await followUser(user?._id);
    isFollowed = true;

    if (res === 'User followed') {
      user.followers.push($me?._id);
      user.followers = user.followers;
    }
  };

  const handleUnfollow = async () => {
    const res = await unfollowUser(user?._id);
    isFollowed = false;
    if (res === 'User unfollowed') {
      user.followers = user.followers.filter(f => f !== $me?._id);
    }
  };

  const handleFollowClick = async () => {
    isHandlingFollow = true;
    if (isFollowed) await handleUnfollow();
    else await handleFollow();
    isHandlingFollow = false;
  };
</script>

<div
  class="{containerCls} bg-white --updated-border rounded-xl overflow-hidden hover:shadow-island cursor-pointer duration-200 w-full max-w-96 h-full"
>
  <div class="flex flex-col justify-between h-full">
    <div class="flex flex-col justify-between">
      {#if user?.coverImage}
        <Media
          on:keydown
          on:click={() => goto(`/profile/${user?.userName}`)}
          src={user.coverImage}
          alt=""
          class="h-20 min-h-[8rem] w-full object-cover "
        />
      {:else}
        <button
          on:keydown
          on:click={() => goto(`/profile/${user?.userName}`)}
          class="h-20 min-h-[8rem] w-full bg-gray-100"
        />
      {/if}

      <div class="flex px-3 gap-x-2 justify-between flex-row -translate-y-3 items-end">
        <div class="flex flex-row gap-3 items-end">
          <!-- Avatar  -->
          <button
            class="rounded-full bg-white"
            on:keydown
            on:click={() => goto(`/profile/${user?.userName}`)}
          >
            <div
              class="w-16 rounded-full aspect-square bg-gray-100 overflow-hidden outline outline-4 outline-white"
            >
              <UserAvatarWrapper
                profileImage={user?.profileImage}
                userName={user?.userName}
                cls={'w-16 h-16'}
              />
            </div>
          </button>
          <!-- Name location and follow button -->
          <div>
            <div class="flex items-center justify-between gap-3 pt-3 pr-5">
              <button
                on:click={() => goto(`/profile/${user?.userName}`)}
                class="font-medium empty:bg-gray-100 empty:w-20 rounded text-left hover:underline"
              >
                {user?.name || ''}
              </button>
            </div>

            <div class="flex gap-x-2">
              <div
                class="text-xs text-blueish-grey-500 empty:bg-gray-100 empty:w-20 rounded hover:underline"
              >
                @{user?.userName || ''}
              </div>

              {#if user?.location}
                <div class="flex text-xs gap-x-1">
                  <Location />
                  <div class="text-blueish-grey-500 empty:bg-gray-100 empty:w-16 whitespace-nowrap">
                    {user?.location || ''}
                  </div>
                </div>
              {/if}
            </div>
          </div>
        </div>

        <!-- Follow button -->
        {#if displayFollow}
          <button
            class="border self-center disabled:text-gray-300 disabled:border-gray-200 mt-2 mr-2 disabled:bg-white rounded-lg h-8 border-brand-green text-brand-green px-2 text-sm hover:bg-green-50 duration-200 focus:outline outline-[#0F9D5833] hover:enabled:shadow-md"
            disabled={!user || isHandlingFollow}
            on:click={handleFollowClick}
          >
            {isFollowed ? 'Unfollow' : 'Follow'}
          </button>
        {/if}
      </div>

      <!-- Tags -->
      {#if user.interests?.length}
        <div class="flex gap-2 px-4 flex-wrap mb-2">
          <!-- {#each user.tags as tag} -->
          {#each user?.interests as tag}
            <DefaultTag corners size="sm">
              {tag}
            </DefaultTag>
          {/each}
        </div>
      {/if}

      <!-- Stats -->
      <div class="flex px-3 text-garden-grey-600 gap-x-4 flex-wrap text-sm">
        <div class="flex items-center gap-2">
          <div class="text-garden-grey-800 empty:bg-gray-100 empty:w-4 rounded">
            {user?.userTotalPosts || '0'}
          </div>
          Posts
        </div>

        <div class="flex gap-2 items-center">
          <div class="text-garden-grey-800 empty:bg-gray-100 empty:w-4 rounded">
            {user?.followersAmount || '0'}
          </div>
          <p>Followers</p>
        </div>

        <div class="flex items-center gap-2">
          <div class="text-garden-grey-800 empty:bg-gray-100 empty:w-4 rounded">
            {user?.userTotalEarnedSeed || '0'}
          </div>
          Seeds
        </div>
      </div>

      <!-- Bio -->
      <div class="mt-1 px-3">
        <div class="text-blueish-grey-700 text-xs w-full empty:bg-gray-100 empty:h-12 rounded-md">
          {user?.about || ''}
        </div>
      </div>
    </div>

    <div>
      <div class="grid grid-cols-3 gap-x-2 p-3">
        <!-- {#each user.featuredPosts.slice(0, 3) as featuredPost} -->
        {#if user.firstPosts && Array.isArray(user.firstPosts)}
          {#each user.firstPosts as index}
            <!-- Need to parse the thumbnail if it's a File later -->
            <button
              class="active:scale-95 duration-200 bg-gray-100 aspect-square rounded-lg overflow-hidden"
              title={index?.itemTitle || ''}
            >
              <Media
                src={index.individualItemThumbnail[0]}
                alt=" "
                class=" outline-gray-200 object-cover h-full w-full duration-200"
              />
            </button>
          {/each}
        {/if}
      </div>
    </div>
  </div>
</div>
